import React, { useMemo, useState } from "react";
import { MdClose, MdReceiptLong } from "react-icons/md";
import { toast } from "react-toastify";
import api from "../../../../services/axios";
import Modal from "../../../../components/modal";
import { ProjectOverview, BillingTypeName } from "../../../../interfaces/Financial";

interface Props {
  clientId: number;
  clientName: string;
  projects: ProjectOverview[];
  month: number;
  year: number;
  onClose: () => void;
  onSuccess: () => void;
}

interface Line {
  checked: boolean;
  amount: number;
}

const fmtMoney = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const BillingComposeModal: React.FC<Props> = ({
  clientId,
  clientName,
  projects,
  month,
  year,
  onClose,
  onSuccess,
}) => {
  const [lines, setLines] = useState<Record<number, Line>>(() => {
    const init: Record<number, Line> = {};
    projects.forEach((p) => {
      const suggested = p.real > 0 ? p.real : p.forecast;
      init[p.projectId] = { checked: suggested > 0, amount: Number(suggested.toFixed(2)) };
    });
    return init;
  });
  const [submitting, setSubmitting] = useState(false);

  const setLine = (id: number, patch: Partial<Line>) =>
    setLines((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const selected = useMemo(
    () => projects.filter((p) => lines[p.projectId]?.checked),
    [projects, lines]
  );
  const total = useMemo(
    () => selected.reduce((s, p) => s + (lines[p.projectId]?.amount || 0), 0),
    [selected, lines]
  );

  const handleGenerate = async () => {
    const payload = {
      clientId,
      monthRef: month,
      yearRef: year,
      projects: selected.map((p) => ({
        projectId: p.projectId,
        amount: lines[p.projectId].amount,
      })),
    };
    setSubmitting(true);
    try {
      await api.post("/Financial/GenerateClientBilling", payload);
      toast.success(
        `Cobrança criada: ${fmtMoney(total)} (${selected.length} projeto(s)). Veja no quadro, em "Pendente de Faturamento".`,
        { position: "top-right", autoClose: 5000 }
      );
      onSuccess();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data;
      toast.error(typeof msg === "string" ? msg : "Falha ao gerar o faturamento.", {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div
        className="flex max-h-[88vh] w-full max-w-xl flex-col overflow-hidden rounded-3xl bg-white shadow-dv-lg dark:bg-navy-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative overflow-hidden bg-dv-gradient px-6 py-5 text-white">
          <div className="absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
          <div className="relative flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 backdrop-blur">
                <MdReceiptLong className="h-6 w-6" aria-hidden="true" />
              </div>
              <div>
                <p className="font-display text-[11px] font-bold uppercase tracking-[0.18em] text-white/70">Nova cobrança</p>
                <h3 className="text-lg font-extrabold leading-tight">{clientName}</h3>
                <p className="text-xs text-white/80">
                  Selecione os projetos que compõem esta cobrança
                </p>
              </div>
            </div>
            <button aria-label="Fechar" onClick={onClose} className="rounded-xl p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white">
              <MdClose className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Lista de projetos */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {projects.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">Este cliente não tem projetos ativos.</p>
          ) : (
            <div className="flex flex-col gap-2.5">
              {projects.map((p) => {
                const line = lines[p.projectId];
                return (
                  <label
                    key={p.projectId}
                    className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-3 transition-all ${
                      line.checked
                        ? "border-dv-blue-400 bg-dv-blue-50/60 dark:border-dv-blue-400/50 dark:bg-dv-blue-400/10"
                        : "border-gray-200 hover:border-gray-300 dark:border-white/10 dark:hover:border-white/20"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={line.checked}
                      onChange={(e) => setLine(p.projectId, { checked: e.target.checked })}
                      className="h-5 w-5 shrink-0 accent-dv-blue-600"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-navy-700 dark:text-white">
                        {p.projectName}
                      </p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        {p.billingType ? BillingTypeName[p.billingType] : "—"} ·{" "}
                        {p.hoursThisMonth.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} h no mês
                      </p>
                    </div>
                    <div className="flex items-center gap-1 rounded-xl border border-gray-200 bg-white px-2 py-1.5 dark:border-white/10 dark:bg-navy-900">
                      <span className="text-xs text-gray-400">R$</span>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={line.amount}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setLine(p.projectId, { amount: Number(e.target.value) })}
                        disabled={!line.checked}
                        className="w-24 bg-transparent text-right text-sm font-semibold tabular-nums text-navy-700 outline-none disabled:opacity-40 dark:text-white"
                      />
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-6 py-4 dark:border-gray-700">
          <div className="mb-3 flex items-baseline justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Total ({selected.length} projeto{selected.length === 1 ? "" : "s"})
            </span>
            <span className="font-display text-2xl font-extrabold tabular-nums tracking-tight text-navy-700 dark:text-white">
              {fmtMoney(total)}
            </span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
            >
              Cancelar
            </button>
            <button
              onClick={handleGenerate}
              disabled={submitting || selected.length === 0 || total <= 0}
              className="dv-sheen flex-[2] rounded-xl bg-dv-gradient py-2.5 text-sm font-bold text-white shadow-dv-glow transition-all active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {submitting ? "Criando..." : "Criar cobrança"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default BillingComposeModal;
